import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Building2,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  RefreshCw,
  Mail,
  Pencil,
  Users,
  XCircle,
  Hourglass,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  CampaignStatus,
  AdminCampaignListItemResponse,
  AdminWorkspaceCampaignDetailResponse,
  CampaignSettingResponse,
  ListAdminCampaignsApiAdminCampaignsGetParams,
  AdminWorkspaceEmailItemResponse,
  SequenceStepRequest,
  SequenceStepResponse,
} from "@/lib/api/generated";
import {
  getAdminCampaignsServer,
  getAdminWorkspaceCampaignDetailServer,
  getAdminWorkspaceCampaignHealthCheckServer,
  getAdminWorkspaceCampaignSettingServer,
  getAdminWorkspaceEmailsServer,
  updateAdminWorkspaceCampaignBusinessServer,
  updateAdminWorkspaceCampaignIcpServer,
  updateAdminWorkspaceCampaignSequenceServer,
  type AdminCampaignHealthCheckResponse,
  type AdminWorkspaceEmailListResponse,
} from "@/server/admin";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmailDetailDialog } from "@/components/admin/email-detail-dialog";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError } from "@/lib/structured-log";

interface SearchParams {
  page?: number;
  status?: CampaignStatus | "all";
  query?: string;
  campaignId?: string;
  sortBy?: "updated_at" | "review_approval_rate" | "last_message_sent_at";
  sortOrder?: "asc" | "desc";
  hasSentMessages?: boolean;
  lastMessageSentDaysAgo?: number;
}

const PAGE_SIZE = 20;

// Business form types
interface BusinessFormValues {
  name: string;
  location: string;
  website?: string;
  sellingPointsText?: string;
  productsText?: string;
}

// ICP form types
interface IcpFormValues {
  searchQuery: string;
  source?: string;
}

type SequenceEditorDelayUnit = "hours" | "days";
type SequenceEditorChannel = "email" | "linkedin" | "whatsapp";
type SequenceEditorMessageType = "new_thread" | "reply";

interface SequenceEditorStep {
  clientId: string;
  stepOrder: number;
  channel: SequenceEditorChannel;
  delayAmount: number;
  delayUnit: SequenceEditorDelayUnit;
  messageType: SequenceEditorMessageType;
  subject: string;
  template: string;
}

function createClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeSequenceEditorSteps(steps: SequenceEditorStep[]): SequenceEditorStep[] {
  return steps.map((step, index) => ({
    ...step,
    stepOrder: index + 1,
  }));
}

function toMultiline(values: string[] | null | undefined): string {
  if (!values || values.length === 0) return "";
  return values.join("\n");
}

function buildBusinessDefaults(
  detail: AdminWorkspaceCampaignDetailResponse | null,
): BusinessFormValues {
  return {
    name: detail?.business?.name ?? "",
    location: detail?.business?.location ?? "",
    website: detail?.business?.website ?? "",
    sellingPointsText: toMultiline(detail?.business?.selling_points),
    productsText: toMultiline(detail?.business?.products),
  };
}

function buildIcpDefaults(detail: AdminWorkspaceCampaignDetailResponse | null): IcpFormValues {
  return {
    searchQuery: detail?.icp?.search_query ?? "",
    source: detail?.icp?.source ?? "",
  };
}

type CampaignSettingWithSequence = CampaignSettingResponse & {
  sequence?: SequenceStepResponse[] | null;
};

function getCampaignSettingSequence(
  setting: CampaignSettingResponse | null,
): SequenceStepResponse[] {
  const sequence = (setting as CampaignSettingWithSequence | null)?.sequence;
  return Array.isArray(sequence) ? sequence : [];
}

function toSequenceEditorSteps(setting: CampaignSettingResponse | null): SequenceEditorStep[] {
  return normalizeSequenceEditorSteps(
    getCampaignSettingSequence(setting)
      .slice()
      .sort((a: SequenceStepResponse, b: SequenceStepResponse) => a.id.localeCompare(b.id))
      .map((step: SequenceStepResponse) => ({
        clientId: step.id || createClientId(),
        stepOrder: 0,
        channel:
          step.channel === "linkedin" || step.channel === "whatsapp" ? step.channel : "email",
        delayAmount: Number.isFinite(step.delay?.amount) ? step.delay.amount : 0,
        delayUnit: String(step.delay?.unit || "days").toLowerCase() === "hours" ? "hours" : "days",
        messageType:
          String(step.message_type || "new_thread").toLowerCase() === "reply"
            ? "reply"
            : "new_thread",
        subject: step.subject ?? "",
        template: step.template ?? "",
      })),
  );
}

export const Route = createFileRoute("/admin/campaign")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as CampaignStatus | "all") || "all",
    query: typeof search.query === "string" ? search.query : undefined,
    campaignId: typeof search.campaignId === "string" ? search.campaignId : undefined,
    sortBy: ["updated_at", "review_approval_rate", "last_message_sent_at"].includes(
      search.sortBy as string,
    )
      ? (search.sortBy as "updated_at" | "review_approval_rate" | "last_message_sent_at")
      : "updated_at",
    sortOrder: search.sortOrder === "asc" ? "asc" : "desc",
    hasSentMessages:
      search.hasSentMessages === true ||
      search.hasSentMessages === "true" ||
      search.hasSentMessages === false ||
      search.hasSentMessages === "false"
        ? search.hasSentMessages === true || search.hasSentMessages === "true"
        : undefined,
    lastMessageSentDaysAgo:
      typeof search.lastMessageSentDaysAgo === "number"
        ? search.lastMessageSentDaysAgo
        : typeof search.lastMessageSentDaysAgo === "string"
          ? Number(search.lastMessageSentDaysAgo) || undefined
          : undefined,
  }),
  loaderDeps: ({ search }) => ({
    page: search.page || 1,
    status: search.status || "all",
    query: search.query,
    sortBy: search.sortBy || "updated_at",
    sortOrder: search.sortOrder || "desc",
    hasSentMessages: search.hasSentMessages,
    lastMessageSentDaysAgo: search.lastMessageSentDaysAgo,
  }),
  loader: async ({ deps }) => {
    const skip = ((deps.page || 1) - 1) * PAGE_SIZE;

    // Build params object, only including status if not "all"
    const params: ListAdminCampaignsApiAdminCampaignsGetParams = {
      skip,
      limit: PAGE_SIZE,
    };

    if (deps.status !== "all") {
      params.status = deps.status;
    }

    if (deps.query) {
      params.query = deps.query;
    }

    params.sort_by = deps.sortBy;
    params.sort_order = deps.sortOrder;

    if (deps.hasSentMessages !== undefined) {
      params.has_sent_messages = deps.hasSentMessages;
    }

    if (deps.lastMessageSentDaysAgo !== undefined) {
      params.last_message_sent_days_ago = deps.lastMessageSentDaysAgo;
    }

    const [response, activeWithMessagesResp, totalActiveResp] = await Promise.all([
      getAdminCampaignsServer({ data: params }),
      getAdminCampaignsServer({
        data: { skip: 0, limit: 1, status: "active", has_sent_messages: true },
      }),
      getAdminCampaignsServer({ data: { skip: 0, limit: 1, status: "active" } }),
    ]);

    return {
      campaigns: response as { items: AdminCampaignListItemResponse[]; total: number },
      activeWithMessagesCount: (activeWithMessagesResp as { total: number }).total,
      totalActiveCount: (totalActiveResp as { total: number }).total,
    };
  },
  component: CampaignAdminPage,
});

function CampaignAdminPage() {
  const { t } = useTranslation("admin");
  const { campaigns, activeWithMessagesCount, totalActiveCount } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const currentPage = search.page || 1;
  const currentStatus = search.status || "all";
  const currentQuery = search.query || "";
  const currentCampaignId = search.campaignId;
  const currentSortBy = search.sortBy || "updated_at";
  const currentSortOrder = search.sortOrder || "desc";
  const currentHasSentMessages = search.hasSentMessages;
  const currentLastMessageSentDaysAgo = search.lastMessageSentDaysAgo;

  const [queryInput, setQueryInput] = useState(currentQuery);
  const [lastMessageDaysInput, setLastMessageDaysInput] = useState(
    currentLastMessageSentDaysAgo?.toString() ?? "2",
  );
  const [isSidebarFiltersCollapsed, setIsSidebarFiltersCollapsed] = useState(false);

  // Campaign detail state
  const [campaignDetail, setCampaignDetail] = useState<AdminWorkspaceCampaignDetailResponse | null>(
    null,
  );
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  // Business editing state
  const [isBusinessEditing, setIsBusinessEditing] = useState(false);
  const [isBusinessSaving, setIsBusinessSaving] = useState(false);
  const [sellingPointsLines, setSellingPointsLines] = useState<string[]>([""]);
  const [productLines, setProductLines] = useState<string[]>([""]);

  // ICP editing state
  const [isIcpEditing, setIsIcpEditing] = useState(false);
  const [isIcpSaving, setIsIcpSaving] = useState(false);

  // Sequence setting state
  const [campaignSetting, setCampaignSetting] = useState<CampaignSettingResponse | null>(null);
  const [isCampaignSettingLoading, setIsCampaignSettingLoading] = useState(false);
  const [isSequenceEditing, setIsSequenceEditing] = useState(false);
  const [isSequenceSaving, setIsSequenceSaving] = useState(false);
  const [sequenceSteps, setSequenceSteps] = useState<SequenceEditorStep[]>([]);
  const [settingVersion, setSettingVersion] = useState(0);
  const [campaignHealth, setCampaignHealth] = useState<AdminCampaignHealthCheckResponse | null>(
    null,
  );
  const [isCampaignHealthLoading, setIsCampaignHealthLoading] = useState(false);
  const [campaignHealthError, setCampaignHealthError] = useState<string | null>(null);

  // Email list state
  const [emails, setEmails] = useState<AdminWorkspaceEmailListResponse | null>(null);
  const [emailPage, setEmailPage] = useState(1);
  const [isEmailsLoading, setIsEmailsLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<AdminWorkspaceEmailItemResponse | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(campaigns.total / PAGE_SIZE));
  }, [campaigns.total]);

  const activeSidebarFilterCount = useMemo(() => {
    let count = 0;
    if (currentQuery.trim().length > 0) count += 1;
    if (currentStatus !== "all") count += 1;
    if (currentSortBy !== "updated_at" || currentSortOrder !== "desc") count += 1;
    if (currentHasSentMessages !== undefined) count += 1;
    if (currentLastMessageSentDaysAgo !== undefined) count += 1;
    return count;
  }, [
    currentHasSentMessages,
    currentLastMessageSentDaysAgo,
    currentQuery,
    currentSortBy,
    currentSortOrder,
    currentStatus,
  ]);

  // Auto-select first campaign if none selected
  useEffect(() => {
    if (currentCampaignId || campaigns.items.length === 0) return;

    navigate({
      search: {
        page: currentPage,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: campaigns.items[0]?.id,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
      replace: true,
    });
  }, [
    currentPage,
    currentStatus,
    currentQuery,
    currentCampaignId,
    campaigns.items,
    navigate,
    currentSortBy,
    currentSortOrder,
    currentHasSentMessages,
    currentLastMessageSentDaysAgo,
  ]);

  const selectedCampaign = useMemo(() => {
    if (!currentCampaignId) return null;
    return campaigns.items.find((c) => c.id === currentCampaignId) || null;
  }, [currentCampaignId, campaigns.items]);

  // Load campaign detail when selected
  useEffect(() => {
    if (!selectedCampaign) {
      setCampaignDetail(null);
      setCampaignHealth(null);
      setCampaignHealthError(null);
      setIsCampaignHealthLoading(false);
      return;
    }

    let cancelled = false;
    setIsDetailLoading(true);

    getAdminWorkspaceCampaignDetailServer({
      data: {
        workspaceId: selectedCampaign.workspace_id,
        campaignId: selectedCampaign.id,
      },
    })
      .then((result) => {
        if (cancelled) return;
        setCampaignDetail(result as AdminWorkspaceCampaignDetailResponse);
      })
      .catch((error) => {
        if (cancelled) return;
        logError("admin.campaign.detail_load_failed", {
          campaign_id: selectedCampaign.id,
          error: error instanceof Error ? error.message : "unknown-error",
        });
        setCampaignDetail(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCampaign]);

  useEffect(() => {
    setCampaignHealth(null);
    setCampaignHealthError(null);
    setIsCampaignHealthLoading(false);
  }, [selectedCampaign?.id]);

  // Load campaign setting when selected
  useEffect(() => {
    if (!selectedCampaign) {
      setCampaignSetting(null);
      setSequenceSteps([]);
      setIsSequenceEditing(false);
      return;
    }

    let cancelled = false;
    setIsCampaignSettingLoading(true);

    getAdminWorkspaceCampaignSettingServer({
      data: { workspaceId: selectedCampaign.workspace_id, campaignId: selectedCampaign.id },
    })
      .then((result) => {
        if (cancelled) return;
        const setting = (result as CampaignSettingResponse | null) ?? null;
        setCampaignSetting(setting);
        setSequenceSteps(toSequenceEditorSteps(setting));
        setIsSequenceEditing(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logError("admin.campaign.setting_load_failed", {
          campaign_id: selectedCampaign.id,
          error: error instanceof Error ? error.message : "unknown-error",
        });
        setCampaignSetting(null);
        setSequenceSteps([]);
        setIsSequenceEditing(false);
      })
      .finally(() => {
        if (cancelled) return;
        setIsCampaignSettingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCampaign, settingVersion]);

  // Load emails when campaign is selected
  useEffect(() => {
    if (!selectedCampaign) {
      setEmails(null);
      return;
    }

    let cancelled = false;
    setIsEmailsLoading(true);

    getAdminWorkspaceEmailsServer({
      data: {
        workspaceId: selectedCampaign.workspace_id,
        campaignId: selectedCampaign.id,
        skip: (emailPage - 1) * 20,
        limit: 20,
      },
    })
      .then((result) => {
        if (cancelled) return;
        setEmails(result as AdminWorkspaceEmailListResponse);
      })
      .catch((error) => {
        if (cancelled) return;
        logError("admin.campaign.emails_load_failed", {
          campaign_id: selectedCampaign.id,
          error: error instanceof Error ? error.message : "unknown-error",
        });
        setEmails(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsEmailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCampaign, emailPage]);

  // Reset forms when detail changes
  useEffect(() => {
    businessForm.reset(buildBusinessDefaults(campaignDetail));
    icpForm.reset(buildIcpDefaults(campaignDetail));
    setIsBusinessEditing(false);
    setIsIcpEditing(false);
    setSellingPointsLines(
      campaignDetail?.business?.selling_points?.length
        ? campaignDetail.business.selling_points
        : [""],
    );
    setProductLines(
      campaignDetail?.business?.products?.length ? campaignDetail.business.products : [""],
    );
    // Forms are stable references; only reset when detail changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDetail]);

  // Business form
  const businessFormSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.businessNameRequired")),
        location: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.locationRequired")),
        website: z.string().trim().optional(),
        sellingPointsText: z.string().optional(),
        productsText: z.string().optional(),
      }),
    [t],
  );

  const businessForm = useForm<BusinessFormValues>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: buildBusinessDefaults(campaignDetail),
  });

  // ICP form
  const icpFormSchema = useMemo(
    () =>
      z.object({
        searchQuery: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.searchQueryRequired")),
        source: z.string().trim().optional(),
      }),
    [t],
  );

  const icpForm = useForm<IcpFormValues>({
    resolver: zodResolver(icpFormSchema),
    defaultValues: buildIcpDefaults(campaignDetail),
  });

  const handleBusinessEdit = () => {
    businessForm.reset(buildBusinessDefaults(campaignDetail));
    setSellingPointsLines(
      campaignDetail?.business?.selling_points?.length
        ? campaignDetail.business.selling_points
        : [""],
    );
    setProductLines(
      campaignDetail?.business?.products?.length ? campaignDetail.business.products : [""],
    );
    setIsBusinessEditing(true);
  };

  const handleBusinessCancel = () => {
    businessForm.reset(buildBusinessDefaults(campaignDetail));
    setSellingPointsLines(
      campaignDetail?.business?.selling_points?.length
        ? campaignDetail.business.selling_points
        : [""],
    );
    setProductLines(
      campaignDetail?.business?.products?.length ? campaignDetail.business.products : [""],
    );
    setIsBusinessEditing(false);
  };

  const handleIcpEdit = () => {
    icpForm.reset(buildIcpDefaults(campaignDetail));
    setIsIcpEditing(true);
  };

  const handleIcpCancel = () => {
    icpForm.reset(buildIcpDefaults(campaignDetail));
    setIsIcpEditing(false);
  };

  const handleSequenceEdit = () => {
    if (!campaignSetting) {
      toast.error(t("workspaces.campaignDetail.sequence.errors.settingMissing"));
      return;
    }

    setSequenceSteps(toSequenceEditorSteps(campaignSetting));
    setIsSequenceEditing(true);
  };

  const handleSequenceCancel = () => {
    setSequenceSteps(toSequenceEditorSteps(campaignSetting));
    setIsSequenceEditing(false);
  };

  const handleSequenceStepUpdate = (clientId: string, patch: Partial<SequenceEditorStep>) => {
    setSequenceSteps((prev) =>
      prev.map((step) => (step.clientId === clientId ? { ...step, ...patch } : step)),
    );
  };

  const handleSequenceAddStep = () => {
    setSequenceSteps((prev) =>
      prev.length >= 5
        ? prev
        : normalizeSequenceEditorSteps([
            ...prev,
            {
              clientId: createClientId(),
              stepOrder: prev.length + 1,
              channel: "email",
              delayAmount: 0,
              delayUnit: "days",
              messageType: "new_thread",
              subject: "",
              template: "",
            },
          ]),
    );
  };

  const handleSequenceRemoveStep = (clientId: string) => {
    setSequenceSteps((prev) =>
      normalizeSequenceEditorSteps(prev.filter((s) => s.clientId !== clientId)),
    );
  };

  const handleSequenceSubmit = async () => {
    if (!selectedCampaign) return;
    if (!campaignSetting) {
      toast.error(t("workspaces.campaignDetail.sequence.errors.settingMissing"));
      return;
    }

    const trimmed = sequenceSteps.map((step) => ({
      ...step,
      subject: step.subject.trim(),
      template: step.template.trim(),
    }));

    const invalidTemplate = trimmed.some((step) => step.template.length === 0);
    if (invalidTemplate) {
      toast.error(t("workspaces.campaignDetail.sequence.errors.templateRequired"));
      return;
    }

    const requestSequence: SequenceStepRequest[] = trimmed.map((step) => ({
      channel: "email",
      delay: {
        amount: Number.isFinite(step.delayAmount) ? Math.max(0, Math.trunc(step.delayAmount)) : 0,
        unit: step.delayUnit,
      },
      message_type: step.messageType,
      subject: step.subject.length > 0 ? step.subject : undefined,
      template: step.template,
    }));

    setIsSequenceSaving(true);
    try {
      await updateAdminWorkspaceCampaignSequenceServer({
        data: {
          workspaceId: selectedCampaign.workspace_id,
          campaignId: selectedCampaign.id,
          sequence: requestSequence,
        },
      });

      toast.success(t("workspaces.campaignDetail.sequence.updateSuccess"));
      setIsSequenceEditing(false);
      // Re-fetch the campaign setting to refresh the sequence list from the server
      setSettingVersion((v) => v + 1);
    } catch (submitError) {
      const message = extractApiErrorMessage(
        submitError,
        t("workspaces.campaignDetail.sequence.updateError"),
      );
      logError("admin.campaign.sequence_update_failed", {
        campaign_id: selectedCampaign.id,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsSequenceSaving(false);
    }
  };

  const handleBusinessSubmit = businessForm.handleSubmit(async (values) => {
    if (!selectedCampaign || !campaignDetail) return;

    setIsBusinessSaving(true);
    try {
      const updated = await updateAdminWorkspaceCampaignBusinessServer({
        data: {
          workspaceId: selectedCampaign.workspace_id,
          campaignId: selectedCampaign.id,
          payload: {
            name: values.name.trim(),
            location: values.location.trim(),
            website: values.website && values.website.trim() !== "" ? values.website.trim() : null,
            selling_points: sellingPointsLines
              .map((item) => item.trim())
              .filter((item) => item.length > 0),
            products: productLines.map((item) => item.trim()).filter((item) => item.length > 0),
          },
        },
      });

      setCampaignDetail(updated as AdminWorkspaceCampaignDetailResponse);
      toast.success(t("workspaces.campaignDetail.business.updateSuccess"));
      setIsBusinessEditing(false);
    } catch (submitError) {
      const message = extractApiErrorMessage(
        submitError,
        t("workspaces.campaignDetail.business.updateError"),
      );
      logError("admin.campaign.business_update_failed", {
        campaign_id: selectedCampaign.id,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsBusinessSaving(false);
    }
  });

  const handleIcpSubmit = icpForm.handleSubmit(async (values) => {
    if (!selectedCampaign || !campaignDetail) return;

    setIsIcpSaving(true);
    try {
      const updated = await updateAdminWorkspaceCampaignIcpServer({
        data: {
          workspaceId: selectedCampaign.workspace_id,
          campaignId: selectedCampaign.id,
          payload: {
            search_query: values.searchQuery.trim(),
            source: campaignDetail?.icp?.source ?? null,
          },
        },
      });

      setCampaignDetail(updated as AdminWorkspaceCampaignDetailResponse);
      toast.success(t("workspaces.campaignDetail.icp.updateSuccess"));
      setIsIcpEditing(false);
    } catch (submitError) {
      const message = extractApiErrorMessage(
        submitError,
        t("workspaces.campaignDetail.icp.updateError"),
      );
      logError("admin.campaign.icp_update_failed", {
        campaign_id: selectedCampaign.id,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsIcpSaving(false);
    }
  });

  const handleCampaignHealthCheck = async () => {
    if (!selectedCampaign) return;

    setIsCampaignHealthLoading(true);
    setCampaignHealthError(null);

    try {
      const result = await getAdminWorkspaceCampaignHealthCheckServer({
        data: {
          workspaceId: selectedCampaign.workspace_id,
          campaignId: selectedCampaign.id,
        },
      });
      setCampaignHealth(result as AdminCampaignHealthCheckResponse);
      toast.success(t("workspaces.campaignDetail.healthCheck.loadSuccess"));
    } catch (loadError) {
      const message = extractApiErrorMessage(
        loadError,
        t("workspaces.campaignDetail.healthCheck.loadError"),
      );
      logError("admin.campaign.health_check_failed", {
        campaign_id: selectedCampaign.id,
        error: message,
      });
      setCampaignHealthError(message);
      toast.error(message);
    } finally {
      setIsCampaignHealthLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    const safePage = Math.min(Math.max(1, newPage), totalPages);
    navigate({
      search: {
        page: safePage,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleStatusChange = (newStatus: string) => {
    navigate({
      search: {
        page: 1,
        status: newStatus as CampaignStatus | "all",
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: queryInput.trim() || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleClearSearch = () => {
    setQueryInput("");
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleSelectCampaign = (campaignId: string) => {
    navigate({
      search: {
        page: currentPage,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleSortChange = (newSortBy: typeof currentSortBy) => {
    // If clicking the same sort field, toggle the order
    const newSortOrder =
      currentSortBy === newSortBy && currentSortOrder === "desc" ? "asc" : "desc";
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: newSortBy,
        sortOrder: newSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleHasSentMessagesChange = (value: string) => {
    const hasSentMessages = value === "true" ? true : value === "false" ? false : undefined;
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages,
        lastMessageSentDaysAgo: currentLastMessageSentDaysAgo,
      },
    });
  };

  const handleLastMessageDaysChange = (e: React.FormEvent) => {
    e.preventDefault();
    const days = lastMessageDaysInput.trim() ? Number(lastMessageDaysInput) : undefined;
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: days && days > 0 ? days : undefined,
      },
    });
  };

  const handleClearDaysFilter = () => {
    setLastMessageDaysInput("");
    navigate({
      search: {
        page: 1,
        status: currentStatus,
        query: currentQuery || undefined,
        campaignId: undefined,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        hasSentMessages: currentHasSentMessages,
        lastMessageSentDaysAgo: undefined,
      },
    });
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
      {/* Sidebar - Campaign List */}
      <aside className="flex h-full min-h-0 flex-col overflow-hidden border-b bg-muted/20 lg:border-b-0 lg:border-r">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">{t("campaign.sidebar.title")}</div>
            <div className="text-xs text-muted-foreground">
              {t("campaign.sidebar.total", { total: campaigns.total })} •{" "}
              {t("campaign.sidebar.pageInfo", { current: currentPage, total: totalPages })}
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {campaigns.items.length}
          </Badge>
        </div>

        {/* Active agent stats */}
        <div className="shrink-0 grid grid-cols-2 gap-2 border-b px-5 py-2">
          <div className="rounded-lg bg-muted/40 px-3 py-1.5 text-center">
            <div className="text-base font-bold leading-none text-foreground">
              {activeWithMessagesCount}
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground leading-tight">
              {t("campaign.stats.activeWithMessages")}
            </div>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-1.5 text-center">
            <div className="text-base font-bold leading-none text-foreground">
              {totalActiveCount}
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground leading-tight">
              {t("campaign.stats.totalActive")}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-b px-5 py-3">
          <button
            type="button"
            onClick={() => setIsSidebarFiltersCollapsed((prev) => !prev)}
            className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-1 text-left"
            aria-expanded={!isSidebarFiltersCollapsed}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{t("campaign.filters.title")}</div>
              <div className="text-xs text-muted-foreground">
                {t("campaign.filters.summary", { count: activeSidebarFilterCount })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeSidebarFilterCount > 0 ? (
                <Badge variant="secondary" className="shrink-0">
                  {activeSidebarFilterCount}
                </Badge>
              ) : null}
              {isSidebarFiltersCollapsed ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </button>

          {!isSidebarFiltersCollapsed ? (
            <div className="mt-3 space-y-3">
              <form onSubmit={handleSearch} className="relative">
                <Input
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  placeholder={t("campaign.sidebar.searchPlaceholder")}
                  className="h-9 pr-8"
                />
                {queryInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 flex items-center pr-2 text-muted-foreground/70 hover:text-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </form>
              <Select value={currentStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("campaign.sidebar.filter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("campaign.filters.all")}</SelectItem>
                  <SelectItem value="active">{t("campaign.filters.active")}</SelectItem>
                  <SelectItem value="paused">{t("campaign.filters.paused")}</SelectItem>
                  <SelectItem value="draft">{t("campaign.filters.draft")}</SelectItem>
                  <SelectItem value="archived">{t("campaign.filters.archived")}</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <SortButton
                  active={currentSortBy === "review_approval_rate"}
                  order={currentSortOrder}
                  onClick={() => handleSortChange("review_approval_rate")}
                  label={t("campaign.sidebar.sortByReviewRate")}
                />
                <SortButton
                  active={currentSortBy === "last_message_sent_at"}
                  order={currentSortOrder}
                  onClick={() => handleSortChange("last_message_sent_at")}
                  label={t("campaign.sidebar.sortByLastMessage")}
                />
              </div>

              <Select
                value={
                  currentHasSentMessages === undefined ? "all" : String(currentHasSentMessages)
                }
                onValueChange={handleHasSentMessagesChange}
              >
                <SelectTrigger className="h-8 w-full text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("campaign.filters.allMessages")}</SelectItem>
                  <SelectItem value="true">{t("campaign.filters.hasSentYes")}</SelectItem>
                  <SelectItem value="false">{t("campaign.filters.hasSentNo")}</SelectItem>
                </SelectContent>
              </Select>

              <form onSubmit={handleLastMessageDaysChange} className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    value={lastMessageDaysInput}
                    onChange={(e) => setLastMessageDaysInput(e.target.value)}
                    className="h-8 max-w-[100px] flex-1 text-xs text-center"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {t("campaign.filters.daysAgo")}
                  </span>
                  {lastMessageDaysInput && (
                    <button
                      type="button"
                      onClick={handleClearDaysFilter}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Button type="submit" variant="secondary" size="sm" className="h-8 w-20 text-xs">
                  {t("campaign.filters.apply")}
                </Button>
              </form>
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {campaigns.items.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 py-12 text-sm text-muted-foreground">
              {t("campaign.sidebar.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {campaigns.items.map((campaign) => (
                <CampaignListRow
                  key={campaign.id}
                  campaign={campaign}
                  isSelected={currentCampaignId === campaign.id}
                  onSelect={handleSelectCampaign}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t px-5 py-4">
          <div className="text-xs text-muted-foreground">
            {t("campaign.sidebar.showing", { count: campaigns.items.length })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t("campaign.sidebar.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || campaigns.items.length < PAGE_SIZE}
            >
              {t("campaign.sidebar.next")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content - Campaign Detail */}
      <main className="relative min-h-0 min-w-0 overflow-hidden bg-background">
        {isDetailLoading && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-px overflow-hidden bg-border/80">
            <div className="h-full w-1/3 animate-pulse bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
          </div>
        )}
        <div className="h-full overflow-auto px-4 py-4 lg:px-6 lg:py-5">
          <CampaignDetailCard
            campaign={selectedCampaign}
            detail={campaignDetail}
            isLoading={isDetailLoading}
            businessForm={businessForm}
            icpForm={icpForm}
            isBusinessEditing={isBusinessEditing}
            isIcpEditing={isIcpEditing}
            isBusinessSaving={isBusinessSaving}
            isIcpSaving={isIcpSaving}
            campaignSetting={campaignSetting}
            isCampaignSettingLoading={isCampaignSettingLoading}
            isSequenceEditing={isSequenceEditing}
            isSequenceSaving={isSequenceSaving}
            sequenceSteps={sequenceSteps}
            sellingPointsLines={sellingPointsLines}
            productLines={productLines}
            onBusinessEdit={handleBusinessEdit}
            onBusinessCancel={handleBusinessCancel}
            onBusinessSubmit={handleBusinessSubmit}
            onIcpEdit={handleIcpEdit}
            onIcpCancel={handleIcpCancel}
            onIcpSubmit={handleIcpSubmit}
            onSequenceEdit={handleSequenceEdit}
            onSequenceCancel={handleSequenceCancel}
            onSequenceSubmit={handleSequenceSubmit}
            onSequenceStepUpdate={handleSequenceStepUpdate}
            onSequenceAddStep={handleSequenceAddStep}
            onSequenceRemoveStep={handleSequenceRemoveStep}
            onSellingPointsChange={setSellingPointsLines}
            onProductLinesChange={setProductLines}
            campaignHealth={campaignHealth}
            isCampaignHealthLoading={isCampaignHealthLoading}
            campaignHealthError={campaignHealthError}
            onCampaignHealthCheck={handleCampaignHealthCheck}
            emails={emails}
            isEmailsLoading={isEmailsLoading}
            emailPage={emailPage}
            onEmailPageChange={setEmailPage}
            onEmailClick={(email) => {
              setSelectedEmail(email);
              setIsEmailDetailOpen(true);
            }}
            t={t}
          />

          {/* Email Detail Dialog */}
          <EmailDetailDialog
            email={selectedEmail}
            open={isEmailDetailOpen}
            onOpenChange={setIsEmailDetailOpen}
            t={t}
            i18nPrefix="campaign.detail.emailDetail"
          />
        </div>
      </main>
    </div>
  );
}

function SortButton({
  active,
  order,
  onClick,
  label,
}: {
  active: boolean;
  order: string;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-background text-muted-foreground hover:bg-muted/50",
      ].join(" ")}
    >
      <span>{label}</span>
      {active &&
        (order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );
}

function CampaignListRow({
  campaign,
  isSelected,
  onSelect,
  t,
}: {
  campaign: AdminCampaignListItemResponse;
  isSelected: boolean;
  onSelect: (campaignId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const handleSelect = () => {
    onSelect(campaign.id);
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleSelect}
        aria-pressed={isSelected}
        className={[
          "relative w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isSelected ? "bg-muted/60" : "hover:bg-muted/40",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary transition-opacity",
            isSelected ? "opacity-100" : "opacity-0",
          ].join(" ")}
        />
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-medium">{campaign.name || "-"}</div>
              <CampaignStatusBadge status={campaign.status as CampaignStatus} t={t} />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">{t("campaign.entry.workspace")}:</span>{" "}
              {campaign.workspace_name}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground/90">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {campaign.stats.total_companies}
              </span>
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {campaign.stats.emails_sent}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {campaign.stats.run_duration}
              </span>
              {campaign.stats.review_approval_rate !== null && (
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {campaign.stats.review_approval_rate}%
                </span>
              )}
              {campaign.stats.last_message_sent_at && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(campaign.stats.last_message_sent_at).toLocaleDateString()}
                </span>
              )}
              {!campaign.stats.last_message_sent_at && (
                <span className="flex items-center gap-1 text-amber-500">
                  <Clock className="h-3 w-3" />
                  {t("campaign.sidebar.noMessages")}
                </span>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <span
              className={[
                "inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium",
                isSelected
                  ? "border-transparent bg-secondary text-secondary-foreground"
                  : "border-border bg-background text-foreground",
              ].join(" ")}
            >
              {isSelected ? t("campaign.sidebar.selected") : t("campaign.sidebar.view")}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}

function CampaignDetailCard({
  campaign,
  detail,
  isLoading,
  businessForm,
  icpForm,
  isBusinessEditing,
  isIcpEditing,
  isBusinessSaving,
  isIcpSaving,
  campaignSetting,
  isCampaignSettingLoading,
  isSequenceEditing,
  isSequenceSaving,
  sequenceSteps,
  sellingPointsLines,
  productLines,
  onBusinessEdit,
  onBusinessCancel,
  onBusinessSubmit,
  onIcpEdit,
  onIcpCancel,
  onIcpSubmit,
  onSequenceEdit,
  onSequenceCancel,
  onSequenceSubmit,
  onSequenceStepUpdate,
  onSequenceAddStep,
  onSequenceRemoveStep,
  onSellingPointsChange,
  onProductLinesChange,
  campaignHealth,
  isCampaignHealthLoading,
  campaignHealthError,
  onCampaignHealthCheck,
  emails,
  isEmailsLoading,
  emailPage,
  onEmailPageChange,
  onEmailClick,
  t,
}: {
  campaign: AdminCampaignListItemResponse | null;
  detail: AdminWorkspaceCampaignDetailResponse | null;
  isLoading: boolean;
  businessForm: ReturnType<typeof useForm<BusinessFormValues>>;
  icpForm: ReturnType<typeof useForm<IcpFormValues>>;
  isBusinessEditing: boolean;
  isIcpEditing: boolean;
  isBusinessSaving: boolean;
  isIcpSaving: boolean;
  campaignSetting: CampaignSettingResponse | null;
  isCampaignSettingLoading: boolean;
  isSequenceEditing: boolean;
  isSequenceSaving: boolean;
  sequenceSteps: SequenceEditorStep[];
  sellingPointsLines: string[];
  productLines: string[];
  onBusinessEdit: () => void;
  onBusinessCancel: () => void;
  onBusinessSubmit: () => void;
  onIcpEdit: () => void;
  onIcpCancel: () => void;
  onIcpSubmit: () => void;
  onSequenceEdit: () => void;
  onSequenceCancel: () => void;
  onSequenceSubmit: () => void;
  onSequenceStepUpdate: (clientId: string, patch: Partial<SequenceEditorStep>) => void;
  onSequenceAddStep: () => void;
  onSequenceRemoveStep: (clientId: string) => void;
  onSellingPointsChange: (lines: string[]) => void;
  onProductLinesChange: (lines: string[]) => void;
  campaignHealth: AdminCampaignHealthCheckResponse | null;
  isCampaignHealthLoading: boolean;
  campaignHealthError: string | null;
  onCampaignHealthCheck: () => void;
  emails: AdminWorkspaceEmailListResponse | null;
  isEmailsLoading: boolean;
  emailPage: number;
  onEmailPageChange: (page: number) => void;
  onEmailClick: (email: AdminWorkspaceEmailItemResponse) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (!campaign) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
        {t("campaign.detail.selectPrompt")}
      </div>
    );
  }

  const stats = campaign.stats as AdminCampaignListItemResponse["stats"] & {
    pending_companies_last_updated_at?: string | null;
    contacted_companies_last_updated_at?: string | null;
    qualified_companies_last_updated_at?: string | null;
    rejected_companies_last_updated_at?: string | null;
    emails_sent_last_updated_at?: string | null;
    emails_replied_last_updated_at?: string | null;
  };
  const emailReplyRate =
    stats.emails_sent > 0 ? Math.round((stats.emails_replied / stats.emails_sent) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="space-y-3 border-b pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-tight">
                {campaign.name || t("campaign.detail.untitled")}
              </h2>
              <CampaignStatusBadge status={campaign.status as CampaignStatus} t={t} />
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{campaign.workspace_name}</span>
              <span className="text-muted-foreground/50">•</span>
              <a
                href={`/workspace/${campaign.workspace_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                {campaign.workspace_slug}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <MetaChip
            label={t("campaign.detail.id")}
            value={shortenId(campaign.id)}
            copyValue={campaign.id}
          />
          <MetaChip
            label={t("campaign.detail.workspaceId")}
            value={shortenId(campaign.workspace_id)}
            copyValue={campaign.workspace_id}
          />
          <MetaChip label={t("campaign.detail.created")} value={formatDate(campaign.created_at)} />
          <MetaChip label={t("campaign.detail.updated")} value={formatDate(campaign.updated_at)} />
        </div>
      </header>

      {/* Stats Overview */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={Building2}
          label={t("campaign.stats.totalCompanies")}
          value={stats.total_companies}
          color="blue"
        />
        <StatCard
          icon={Mail}
          label={t("campaign.stats.emailsSent")}
          value={stats.emails_sent}
          color="green"
        />
        <StatCard
          icon={Users}
          label={t("campaign.stats.qualifiedCompanies")}
          value={stats.qualified_companies}
          color="purple"
        />
        <StatCard
          icon={Clock}
          label={t("campaign.stats.runDuration")}
          value={stats.run_duration}
          color="amber"
        />
      </section>

      {/* Company Status Breakdown */}
      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.companyStatusTitle")}
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <CompanyStatusCard
            status="pending"
            count={stats.pending_companies}
            total={stats.total_companies}
            lastUpdatedAt={stats.pending_companies_last_updated_at}
            t={t}
          />
          <CompanyStatusCard
            status="contacted"
            count={stats.contacted_companies}
            total={stats.total_companies}
            lastUpdatedAt={stats.contacted_companies_last_updated_at}
            t={t}
          />
          <CompanyStatusCard
            status="intent"
            count={stats.emails_replied}
            total={stats.total_companies}
            lastUpdatedAt={stats.emails_replied_last_updated_at}
            t={t}
          />
          <CompanyStatusCard
            status="qualified"
            count={stats.qualified_companies}
            total={stats.total_companies}
            lastUpdatedAt={stats.qualified_companies_last_updated_at}
            t={t}
          />
          <CompanyStatusCard
            status="rejected"
            count={stats.rejected_companies}
            total={stats.total_companies}
            lastUpdatedAt={stats.rejected_companies_last_updated_at}
            t={t}
          />
        </div>
      </section>

      {/* Email Performance */}
      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.emailPerformanceTitle")}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <EmailMetricCard
            label={t("campaign.stats.emailsSent")}
            value={stats.emails_sent}
            icon={Mail}
            color="blue"
            lastUpdatedLabel={t("campaign.detail.metricLastUpdated", {
              value: formatDate(stats.emails_sent_last_updated_at),
            })}
          />
          <EmailMetricCard
            label={t("campaign.stats.emailsReplied")}
            value={stats.emails_replied}
            subValue={`${emailReplyRate}%`}
            icon={BarChart3}
            color="purple"
            lastUpdatedLabel={t("campaign.detail.metricLastUpdated", {
              value: formatDate(stats.emails_replied_last_updated_at),
            })}
          />
        </div>
      </section>

      {/* Conversion Funnel */}
      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.conversionFunnelTitle")}
        </h3>
        <div className="rounded-xl border border-border/80 bg-muted/20 p-4">
          <div className="space-y-4">
            <FunnelBar
              label={t("campaign.funnel.companiesFound")}
              value={stats.total_companies}
              max={stats.total_companies}
              color="bg-blue-500"
            />
            <FunnelBar
              label={t("campaign.funnel.contacted")}
              value={stats.contacted_companies}
              max={stats.total_companies}
              color="bg-amber-500"
            />
            <FunnelBar
              label={t("campaign.funnel.replied")}
              value={stats.emails_replied}
              max={stats.total_companies}
              color="bg-green-500"
            />
            <FunnelBar
              label={t("campaign.funnel.qualified")}
              value={stats.qualified_companies}
              max={stats.total_companies}
              color="bg-purple-500"
            />
          </div>
        </div>
      </section>

      <HealthCheckSection
        health={campaignHealth}
        isLoading={isCampaignHealthLoading}
        error={campaignHealthError}
        onRefresh={onCampaignHealthCheck}
        t={t}
      />

      {/* Email List */}
      <EmailListSection
        emails={emails}
        isLoading={isEmailsLoading}
        page={emailPage}
        onPageChange={onEmailPageChange}
        onEmailClick={onEmailClick}
        t={t}
      />

      {/* Business Info & ICP - Only show when detail is loaded */}
      {isLoading ? (
        <div className="space-y-6">
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t("campaign.detail.loadingBusinessAndIcp")}
          </div>
        </div>
      ) : detail ? (
        <div className="divide-y divide-border/80 border-t pt-6">
          {/* Business Profile */}
          <section className="py-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-tight">
                    {t("workspaces.campaignDetail.business.title")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("workspaces.campaignDetail.business.description")}
                  </p>
                </div>
                {isBusinessEditing ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onBusinessCancel}
                      disabled={isBusinessSaving}
                    >
                      {t("workspaces.campaignDetail.business.cancel")}
                    </Button>
                    <Button size="sm" onClick={onBusinessSubmit} disabled={isBusinessSaving}>
                      {isBusinessSaving
                        ? t("workspaces.campaignDetail.business.saving")
                        : t("workspaces.campaignDetail.business.save")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={onBusinessEdit}
                    aria-label={t("workspaces.campaignDetail.business.edit")}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isBusinessEditing ? (
                <form className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">
                        {t("workspaces.campaignDetail.business.fields.name")}
                      </label>
                      <Input {...businessForm.register("name")} />
                      {businessForm.formState.errors.name && (
                        <div className="text-xs text-destructive">
                          {businessForm.formState.errors.name.message}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">
                        {t("workspaces.campaignDetail.business.fields.location")}
                      </label>
                      <Input {...businessForm.register("location")} />
                      {businessForm.formState.errors.location && (
                        <div className="text-xs text-destructive">
                          {businessForm.formState.errors.location.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.website")}
                    </label>
                    <Input {...businessForm.register("website")} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-medium text-muted-foreground">
                        {t("workspaces.campaignDetail.business.fields.sellingPoints")}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onSellingPointsChange([...sellingPointsLines, ""])}
                        disabled={isBusinessSaving}
                      >
                        {t("workspaces.campaignDetail.business.actions.addLine")}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {sellingPointsLines.map((line, index) => (
                        <Input
                          key={`selling-${index}`}
                          value={line}
                          onChange={(e) =>
                            onSellingPointsChange(
                              sellingPointsLines.map((item, i) =>
                                i === index ? e.target.value : item,
                              ),
                            )
                          }
                          placeholder={t(
                            "workspaces.campaignDetail.business.placeholders.sellingPoint",
                            {
                              index: index + 1,
                            },
                          )}
                          disabled={isBusinessSaving}
                        />
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {t("workspaces.campaignDetail.business.hints.clearToRemove")}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-medium text-muted-foreground">
                        {t("workspaces.campaignDetail.business.fields.products")}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onProductLinesChange([...productLines, ""])}
                        disabled={isBusinessSaving}
                      >
                        {t("workspaces.campaignDetail.business.actions.addLine")}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {productLines.map((line, index) => (
                        <Input
                          key={`product-${index}`}
                          value={line}
                          onChange={(e) =>
                            onProductLinesChange(
                              productLines.map((item, i) => (i === index ? e.target.value : item)),
                            )
                          }
                          placeholder={t(
                            "workspaces.campaignDetail.business.placeholders.product",
                            {
                              index: index + 1,
                            },
                          )}
                          disabled={isBusinessSaving}
                        />
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {t("workspaces.campaignDetail.business.hints.clearToRemove")}
                    </div>
                  </div>
                </form>
              ) : detail.business ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.name")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">{detail.business.name}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.location")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">
                      {detail.business.location}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.email")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">
                      {detail.business.email || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.phone")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">
                      {detail.business.phone_number || "-"}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.website")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">
                      {detail.business.website ? (
                        <a
                          href={detail.business.website}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-foreground underline-offset-4 hover:underline"
                        >
                          {detail.business.website}
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        </a>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.sellingPoints")}
                    </div>
                    {detail.business.selling_points.length > 0 ? (
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        {detail.business.selling_points.map((item, index) => (
                          <li key={`selling-${index}`} className="text-sm text-foreground/90">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-1 text-sm text-muted-foreground">-</div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.business.fields.products")}
                    </div>
                    {detail.business.products.length > 0 ? (
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        {detail.business.products.map((item, index) => (
                          <li key={`product-${index}`} className="text-sm text-foreground/90">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-1 text-sm text-muted-foreground">-</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                  {t("workspaces.campaignDetail.business.empty")}
                </div>
              )}
            </div>
          </section>

          {/* ICP Query */}
          <section className="py-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-tight">
                    {t("workspaces.campaignDetail.icp.title")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("workspaces.campaignDetail.icp.description")}
                  </p>
                </div>
                {isIcpEditing ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onIcpCancel}
                      disabled={isIcpSaving}
                    >
                      {t("workspaces.campaignDetail.icp.cancel")}
                    </Button>
                    <Button size="sm" onClick={onIcpSubmit} disabled={isIcpSaving}>
                      {isIcpSaving
                        ? t("workspaces.campaignDetail.icp.saving")
                        : t("workspaces.campaignDetail.icp.save")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={onIcpEdit}
                    aria-label={t("workspaces.campaignDetail.icp.edit")}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isIcpEditing ? (
                <form className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t("workspaces.campaignDetail.icp.fields.searchQuery")}
                    </label>
                    <Textarea {...icpForm.register("searchQuery")} className="min-h-[120px]" />
                    {icpForm.formState.errors.searchQuery && (
                      <div className="text-xs text-destructive">
                        {icpForm.formState.errors.searchQuery.message}
                      </div>
                    )}
                  </div>
                  {detail.icp && (
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {t("workspaces.campaignDetail.icp.fields.source")}
                      </div>
                      <div className="mt-1 text-sm text-foreground/90">
                        {detail.icp.source || "-"}
                      </div>
                    </div>
                  )}
                </form>
              ) : detail.icp ? (
                <div className="space-y-4">
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.icp.fields.source")}
                    </div>
                    <div className="mt-1 text-sm text-foreground/90">
                      {detail.icp.source || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("workspaces.campaignDetail.icp.fields.searchQuery")}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap rounded-xl border border-border/80 bg-background px-4 py-3 text-sm leading-6 text-foreground/90">
                      {detail.icp.search_query}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                  {t("workspaces.campaignDetail.icp.empty")}
                </div>
              )}
            </div>
          </section>

          {/* Sequence */}
          <section className="py-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-tight">
                    {t("workspaces.campaignDetail.sequence.title")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("workspaces.campaignDetail.sequence.description")}
                  </p>
                </div>

                {isSequenceEditing ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSequenceCancel}
                      disabled={isSequenceSaving}
                    >
                      {t("workspaces.campaignDetail.sequence.cancel")}
                    </Button>
                    <Button size="sm" onClick={onSequenceSubmit} disabled={isSequenceSaving}>
                      {isSequenceSaving
                        ? t("workspaces.campaignDetail.sequence.saving")
                        : t("workspaces.campaignDetail.sequence.save")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={onSequenceEdit}
                    aria-label={t("workspaces.campaignDetail.sequence.edit")}
                    disabled={!campaignSetting && !isCampaignSettingLoading}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isCampaignSettingLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  {t("workspaces.campaignDetail.sequence.loading")}
                </div>
              ) : isSequenceEditing ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {t("workspaces.campaignDetail.sequence.hints.keepOrder")}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSequenceAddStep}
                      disabled={sequenceSteps.length >= 5}
                    >
                      {t("workspaces.campaignDetail.sequence.actions.addStep")}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {sequenceSteps.map((step) => (
                      <div key={step.clientId} className="rounded-xl border border-border/80 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold">
                            {t("workspaces.campaignDetail.sequence.stepTitle", {
                              index: step.stepOrder,
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSequenceRemoveStep(step.clientId)}
                            disabled={sequenceSteps.length <= 1}
                          >
                            {t("workspaces.campaignDetail.sequence.actions.removeStep")}
                          </Button>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="space-y-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t("workspaces.campaignDetail.sequence.fields.channel")}
                            </div>
                            <Select
                              value={step.channel}
                              onValueChange={(value) =>
                                onSequenceStepUpdate(step.clientId, {
                                  channel: value as SequenceEditorChannel,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="email">email</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t("workspaces.campaignDetail.sequence.fields.messageType")}
                            </div>
                            <Select
                              value={step.messageType}
                              onValueChange={(value) =>
                                onSequenceStepUpdate(step.clientId, {
                                  messageType: value as SequenceEditorMessageType,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new_thread">new_thread</SelectItem>
                                <SelectItem value="reply">reply</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t("workspaces.campaignDetail.sequence.fields.delay")}
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                value={Number.isFinite(step.delayAmount) ? step.delayAmount : 0}
                                onChange={(e) =>
                                  onSequenceStepUpdate(step.clientId, {
                                    delayAmount: Number(e.target.value),
                                  })
                                }
                              />
                              <Select
                                value={step.delayUnit}
                                onValueChange={(value) =>
                                  onSequenceStepUpdate(step.clientId, {
                                    delayUnit: value as SequenceEditorDelayUnit,
                                  })
                                }
                              >
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="hours">hours</SelectItem>
                                  <SelectItem value="days">days</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t("workspaces.campaignDetail.sequence.fields.subject")}
                            </div>
                            <Input
                              value={step.subject}
                              onChange={(e) =>
                                onSequenceStepUpdate(step.clientId, { subject: e.target.value })
                              }
                            />
                          </div>
                        </div>

                        <div className="mt-4 space-y-1.5">
                          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t("workspaces.campaignDetail.sequence.fields.template")}
                          </div>
                          <Textarea
                            value={step.template}
                            onChange={(e) =>
                              onSequenceStepUpdate(step.clientId, { template: e.target.value })
                            }
                            className="min-h-[140px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : getCampaignSettingSequence(campaignSetting).length > 0 ? (
                <div className="rounded-xl border border-border/80">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[88px]">
                          {t("workspaces.campaignDetail.sequence.table.step")}
                        </TableHead>
                        <TableHead className="w-[110px]">
                          {t("workspaces.campaignDetail.sequence.table.channel")}
                        </TableHead>
                        <TableHead className="w-[120px]">
                          {t("workspaces.campaignDetail.sequence.table.delay")}
                        </TableHead>
                        <TableHead className="w-[140px]">
                          {t("workspaces.campaignDetail.sequence.table.messageType")}
                        </TableHead>
                        <TableHead>
                          {t("workspaces.campaignDetail.sequence.table.subject")}
                        </TableHead>
                        <TableHead>
                          {t("workspaces.campaignDetail.sequence.table.preview")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getCampaignSettingSequence(campaignSetting).map(
                        (step: SequenceStepResponse, index: number) => (
                          <TableRow key={step.id}>
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell className="text-muted-foreground">{step.channel}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {step.delay?.amount ?? 0} {step.delay?.unit ?? "days"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {step.message_type}
                            </TableCell>
                            <TableCell>{step.subject || "-"}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {step.template?.length ? step.template.slice(0, 120) : "-"}
                              {step.template?.length && step.template.length > 120 ? "…" : ""}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                  {t("workspaces.campaignDetail.sequence.empty")}
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function CampaignStatusBadge({
  status,
  t,
}: {
  status: CampaignStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const variants: Record<CampaignStatus, { class: string; label: string }> = {
    active: {
      class: "border-green-200 bg-green-50 text-green-700",
      label: t("campaign.status.active"),
    },
    paused: {
      class: "border-amber-200 bg-amber-50 text-amber-700",
      label: t("campaign.status.paused"),
    },
    archived: {
      class: "border-gray-200 bg-gray-50 text-gray-700",
      label: t("campaign.status.archived"),
    },
    draft: {
      class: "border-blue-200 bg-blue-50 text-blue-700",
      label: t("campaign.status.draft"),
    },
  };

  const variant = variants[status];
  return (
    <Badge variant="outline" className={variant.class}>
      {variant.label}
    </Badge>
  );
}

function HealthCheckSection({
  health,
  isLoading,
  error,
  onRefresh,
  t,
}: {
  health: AdminCampaignHealthCheckResponse | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("workspaces.campaignDetail.healthCheck.title")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("workspaces.campaignDetail.healthCheck.description")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="shrink-0"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          {isLoading
            ? t("workspaces.campaignDetail.healthCheck.loading")
            : t("workspaces.campaignDetail.healthCheck.action")}
        </Button>
      </div>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {!health && !isLoading ? (
        <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
          {t("workspaces.campaignDetail.healthCheck.empty")}
        </div>
      ) : null}

      {health ? (
        <div className="space-y-4 rounded-xl border border-border/80 bg-muted/20 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={
                health.ready
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              }
            >
              {health.ready
                ? t("workspaces.campaignDetail.healthCheck.ready")
                : t("workspaces.campaignDetail.healthCheck.notReady")}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t("workspaces.campaignDetail.healthCheck.agentMode", {
                value: health.agent_mode,
              })}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <HealthMetric
              label={t("workspaces.campaignDetail.healthCheck.metrics.targets")}
              value={health.runtime_entities.target_company_count}
            />
            <HealthMetric
              label={t("workspaces.campaignDetail.healthCheck.metrics.deals")}
              value={health.runtime_entities.deal_count}
            />
            <HealthMetric
              label={t("workspaces.campaignDetail.healthCheck.metrics.sendTasks")}
              value={health.runtime_entities.send_task_count}
            />
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("workspaces.campaignDetail.healthCheck.directReasons")}
            </div>
            <ReasonList
              items={health.direct_reasons}
              emptyLabel={t("workspaces.campaignDetail.healthCheck.none")}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <HealthFactGroup
              title={t("workspaces.campaignDetail.healthCheck.sections.configuration")}
              rows={[
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.sequenceChannels"),
                  value:
                    health.sequence.channels.length > 0
                      ? health.sequence.channels.join(", ")
                      : t("workspaces.campaignDetail.healthCheck.none"),
                },
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.senderIssues"),
                  value:
                    health.sender.missing_reasons.length > 0
                      ? health.sender.missing_reasons.join(", ")
                      : t("workspaces.campaignDetail.healthCheck.none"),
                },
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.icpQueries"),
                  value: String(health.icp.confirmed_icp_count),
                },
              ]}
            />
            <HealthFactGroup
              title={t("workspaces.campaignDetail.healthCheck.sections.runtime")}
              rows={[
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.outbox"),
                  value: String(health.runtime_entities.outbox_count),
                },
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.drafts"),
                  value: String(health.runtime_entities.draft_count),
                },
                {
                  label: t("workspaces.campaignDetail.healthCheck.fields.requiredChannelContacts"),
                  value: String(health.runtime_entities.deals_with_required_channel_contact),
                },
              ]}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <StatusCountList
              title={t("workspaces.campaignDetail.healthCheck.sections.targetStatus")}
              items={health.runtime_entities.target_company_by_status.map((item) => ({
                label: item.status,
                count: item.count,
              }))}
              emptyLabel={t("workspaces.campaignDetail.healthCheck.none")}
            />
            <StatusCountList
              title={t("workspaces.campaignDetail.healthCheck.sections.outboxStatus")}
              items={Object.entries(health.runtime_entities.deal_created_outbox_status_counts).map(
                ([label, count]) => ({
                  label,
                  count,
                }),
              )}
              emptyLabel={t("workspaces.campaignDetail.healthCheck.none")}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function HealthMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border/80 bg-background px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function HealthFactGroup({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div className="rounded-xl border border-border/80 bg-background p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="max-w-[65%] text-right text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusCountList({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items: Array<{ label: string; count: number }>;
  emptyLabel: string;
}) {
  return (
    <div className="rounded-xl border border-border/80 bg-background p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {items.length === 0 ? (
        <div className="mt-3 text-sm text-muted-foreground">{emptyLabel}</div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-foreground">{item.label}</span>
              <span className="font-medium text-foreground">{item.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReasonList({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
  if (items.length === 0) {
    return <div className="text-sm text-muted-foreground">{emptyLabel}</div>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Badge key={item} variant="secondary" className="font-mono text-[11px]">
          {item}
        </Badge>
      ))}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  color: "blue" | "green" | "purple" | "amber";
}) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div className={`rounded-xl border p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function CompanyStatusCard({
  status,
  count,
  total,
  lastUpdatedAt,
  t,
}: {
  status: "pending" | "contacted" | "intent" | "qualified" | "rejected";
  count: number;
  total: number;
  lastUpdatedAt?: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  const config = {
    pending: {
      icon: Hourglass,
      color: "bg-amber-50 text-amber-700 border-amber-200",
      label: t("campaign.companyStatus.pending"),
    },
    contacted: {
      icon: Mail,
      color: "bg-blue-50 text-blue-700 border-blue-200",
      label: t("campaign.companyStatus.contacted"),
    },
    intent: {
      icon: Users,
      color: "bg-indigo-50 text-indigo-700 border-indigo-200",
      label: t("campaign.companyStatus.intent"),
    },
    qualified: {
      icon: CheckCircle,
      color: "bg-green-50 text-green-700 border-green-200",
      label: t("campaign.companyStatus.qualified"),
    },
    rejected: {
      icon: XCircle,
      color: "bg-red-50 text-red-700 border-red-200",
      label: t("campaign.companyStatus.rejected"),
    },
  };

  const { icon: Icon, color, label } = config[status];

  return (
    <div className={`rounded-xl border p-3 ${color}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 opacity-70" />
          <span className="text-[11px] font-medium opacity-80">{label}</span>
        </div>
        <span className="text-[11px] font-medium">{percentage}%</span>
      </div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <div className="text-xl font-semibold">{count}</div>
        <div className="text-[10px] opacity-80">
          {t("campaign.detail.metricLastUpdated", { value: formatDate(lastUpdatedAt) })}
        </div>
      </div>
    </div>
  );
}

function EmailMetricCard({
  label,
  value,
  subValue,
  lastUpdatedLabel,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  subValue?: string;
  lastUpdatedLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  color: "blue" | "green" | "purple" | "amber";
}) {
  const colorClasses = {
    blue: "text-blue-700",
    green: "text-green-700",
    purple: "text-purple-700",
    amber: "text-amber-700",
  };

  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/80 bg-muted/20 p-3">
      <div className={`rounded-lg bg-background p-2 ${colorClasses[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 flex items-end justify-between gap-2">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-semibold">{value}</span>
            {subValue && (
              <span className={`text-sm font-medium ${colorClasses[color]}`}>{subValue}</span>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">{lastUpdatedLabel}</div>
        </div>
      </div>
    </div>
  );
}

function FunnelBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {value} <span className="text-muted-foreground">({percentage}%)</span>
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function MetaChip({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: string;
  copyValue?: string;
}) {
  const copyToClipboard = async (text: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error("Clipboard unavailable");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const rendered = value.trim() === "" ? "-" : value;

  return (
    <div className="inline-flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="shrink-0 uppercase tracking-wide text-muted-foreground/80">{label}:</span>
      <span className="min-w-0 truncate whitespace-nowrap font-mono text-foreground/90">
        {rendered}
      </span>
      {copyValue ? (
        <button
          type="button"
          onClick={() => void copyToClipboard(copyValue)}
          className="shrink-0 text-muted-foreground/70 transition-colors hover:text-foreground"
          aria-label={`Copy ${label}`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

// Utilities
function formatDate(value: string | null | undefined): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  const year = pick("year");
  const month = pick("month");
  const day = pick("day");
  const hour = pick("hour");
  const minute = pick("minute");
  const second = pick("second");
  const timeZoneName = pick("timeZoneName");

  const core = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  return timeZoneName ? `${core} ${timeZoneName}` : core;
}

function shortenId(value: string | null | undefined, prefix = 8, suffix = 4): string {
  if (!value) return "-";
  if (value.length <= prefix + suffix + 1) return value;
  return `${value.slice(0, prefix)}…${value.slice(-suffix)}`;
}

function EmailListSection({
  emails,
  isLoading,
  page,
  onPageChange,
  onEmailClick,
  t,
}: {
  emails: AdminWorkspaceEmailListResponse | null;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onEmailClick: (email: AdminWorkspaceEmailItemResponse) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const totalPages = emails?.total ? Math.max(1, Math.ceil(emails.total / 20)) : 1;

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("campaign.detail.emailListTitle")}
      </h3>
      <div className="overflow-hidden rounded-lg border border-border/80">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("campaign.detail.emails.table.direction")}</TableHead>
              <TableHead>{t("campaign.detail.emails.table.sender")}</TableHead>
              <TableHead>{t("campaign.detail.emails.table.recipients")}</TableHead>
              <TableHead>{t("campaign.detail.emails.table.subject")}</TableHead>
              <TableHead>{t("campaign.detail.emails.table.preview")}</TableHead>
              <TableHead>{t("campaign.detail.emails.table.time")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  {t("campaign.detail.emails.loading")}
                </TableCell>
              </TableRow>
            ) : !emails || emails.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  {t("campaign.detail.emails.empty")}
                </TableCell>
              </TableRow>
            ) : (
              emails.items.map((email) => (
                <TableRow
                  key={email.message_id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => onEmailClick(email)}
                >
                  <TableCell>
                    <EmailDirectionBadge direction={email.direction} />
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[150px] truncate text-sm">
                      {email.sender_display_name}
                    </div>
                    <div className="max-w-[150px] truncate text-xs text-muted-foreground">
                      {email.sender_email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[150px] truncate text-xs text-muted-foreground">
                      {email.recipient_emails && email.recipient_emails.length > 0
                        ? `${email.recipient_emails.slice(0, 2).join(", ")}${
                            email.recipient_emails.length > 2
                              ? ` +${email.recipient_emails.length - 2}`
                              : ""
                          }`
                        : "-"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px] truncate font-medium">{email.subject || "-"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[250px] truncate text-sm text-muted-foreground">
                      {email.body_preview}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(email.sent_at ?? email.received_at ?? email.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {emails && emails.total > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {t("campaign.detail.emails.pageInfo", { current: page, total: totalPages })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || isLoading}
            >
              {t("campaign.detail.emails.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || isLoading}
            >
              {t("campaign.detail.emails.next")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function EmailDirectionBadge({ direction }: { direction: string }) {
  const config: Record<string, { class: string; label: string }> = {
    outbound: {
      class: "bg-blue-50 text-blue-700 border-blue-200",
      label: "Sent",
    },
    inbound: {
      class: "bg-green-50 text-green-700 border-green-200",
      label: "Received",
    },
  };

  const cfg = config[direction] || {
    class: "bg-gray-50 text-gray-700 border-gray-200",
    label: direction,
  };

  return (
    <Badge variant="outline" className={cfg.class}>
      {cfg.label}
    </Badge>
  );
}
